import { useState, useEffect, useCallback } from "react";
import { Loader2, Plus, Search, Power, Phone } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["delivery_agent", "cab_driver", "nurse", "doctor", "vendor"];
const BLANK = { name: "", type: "cab_driver", phone: "", alt_phone: "", vehicle: "", service_area: "", rating: "", notes: "", active: true };

export default function AdminProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (type) params.set("type", type);
      if (q) params.set("q", q);
      const r = await axios.get(`${API}/admin/providers?${params}`);
      setProviders(r.data.providers);
    } catch { toast.error("Failed to load providers"); }
    setLoading(false);
  }, [type, q]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const body = { ...form, rating: form.rating ? Number(form.rating) : null };
      if (form.provider_id) await axios.patch(`${API}/admin/providers/${form.provider_id}`, body);
      else await axios.post(`${API}/admin/providers`, body);
      toast.success("Saved");
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deactivate = async (pid) => {
    await axios.delete(`${API}/admin/providers/${pid}`); toast.success("Deactivated"); load();
  };
  const reveal = async (pid) => {
    const r = await axios.get(`${API}/admin/providers/${pid}`);
    toast.info(`${r.data.name}: ${r.data.phone || "no phone"}`, { duration: 8000 });
  };
  const edit = async (pid) => {
    try {
      const r = await axios.get(`${API}/admin/providers/${pid}`);
      setForm({ ...r.data, rating: r.data.rating || "" });
    } catch { toast.error("Could not load provider"); }
  };

  return (
    <div data-testid="admin-providers">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-semibold text-gray-900 text-lg">Providers</h2>
        <button onClick={() => setForm({ ...BLANK })} className="flex items-center gap-1.5 text-sm bg-[#2D2766] text-white rounded-lg px-3 py-1.5" data-testid="provider-add-btn">
          <Plus className="w-4 h-4" /> Add provider
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={type} onChange={(e) => setType(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="provider-filter-type">
          <option value="">All types</option>
          {TYPES.map((tp) => <option key={tp} value={tp}>{tp.replace("_", " ")}</option>)}
        </select>
        <div className="flex items-center gap-1 border rounded-lg px-2 bg-white">
          <Search className="w-4 h-4 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone" className="py-1.5 text-sm outline-none" data-testid="provider-search" />
        </div>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-gray-400" /> :
        providers.length === 0 ? <p className="text-gray-400 text-center py-10">No providers yet</p> :
        <div className="space-y-2">
          {providers.map((p) => (
            <div key={p.provider_id} className={`bg-white rounded-xl p-3 border shadow-sm flex items-center justify-between gap-3 ${p.active ? "" : "opacity-50"}`} data-testid={`provider-row-${p.provider_id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate">{p.name} <span className="text-xs font-normal text-gray-400">{p.type.replace("_", " ")}</span></p>
                <p className="text-xs text-gray-500">{p.phone_masked || "—"}{p.vehicle ? ` · ${p.vehicle}` : ""}{p.service_area ? ` · ${p.service_area}` : ""}{p.rating ? ` · ⭐${p.rating}` : ""}</p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => reveal(p.provider_id)} className="p-1.5 rounded bg-gray-100 text-gray-600" title="Reveal number" data-testid={`provider-reveal-${p.provider_id}`}><Phone className="w-4 h-4" /></button>
                <button onClick={() => edit(p.provider_id)} className="px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs" data-testid={`provider-edit-${p.provider_id}`}>Edit</button>
                {p.active && <button onClick={() => deactivate(p.provider_id)} className="p-1.5 rounded bg-red-50 text-red-600" title="Deactivate"><Power className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>}

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="provider-form">
          <div className="absolute inset-0 bg-black/40" onClick={() => setForm(null)} />
          <div className="relative bg-white rounded-2xl p-5 w-full max-w-md shadow-2xl space-y-2">
            <h3 className="font-semibold text-gray-900">{form.provider_id ? "Edit" : "Add"} provider</h3>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="border rounded-lg px-3 py-2 w-full text-sm" data-testid="provider-name-input" />
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border rounded-lg px-3 py-2 w-full text-sm bg-white" data-testid="provider-type-input">
              {TYPES.map((tp) => <option key={tp} value={tp}>{tp.replace("_", " ")}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="border rounded-lg px-3 py-2 text-sm" data-testid="provider-phone-input" />
              <input value={form.alt_phone} onChange={(e) => setForm({ ...form, alt_phone: e.target.value })} placeholder="Alt phone" className="border rounded-lg px-3 py-2 text-sm" />
              <input value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} placeholder="Vehicle / reg" className="border rounded-lg px-3 py-2 text-sm" />
              <input value={form.service_area} onChange={(e) => setForm({ ...form, service_area: e.target.value })} placeholder="Service area" className="border rounded-lg px-3 py-2 text-sm" />
              <input value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} placeholder="Rating" className="border rounded-lg px-3 py-2 text-sm" />
            </div>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" rows={2} className="border rounded-lg px-3 py-2 w-full text-sm" />
            <div className="flex gap-2 justify-end pt-1">
              <button onClick={() => setForm(null)} className="px-4 py-2 rounded-lg text-sm text-gray-600">Cancel</button>
              <button onClick={save} disabled={!form.name.trim()} className="px-4 py-2 rounded-lg text-sm bg-[#2D2766] text-white disabled:opacity-50" data-testid="provider-save-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
